// Batch 13 Part C Step 4 — badges rewired for the B13 palette and,
// for prompt levels, sourced from the stored per-level colour rather
// than a name hash. Every colour is derived from the shared token
// set — no inline hex. Consumers pass raw values in and the mapping
// stays in one place.
import React from "react";
import { Box, Tooltip } from "@mui/material";
import { LegacyTokens, B13, B13Ink, B13Status, tint12 } from "../../../theme/b13Tokens";

// ── Session status ──────────────────────────────────────────────────
// session_data_records.status is a closed enum. Alias table maps each
// key to a semantic accent from the B13 palette. Unknown values render
// a neutral chip with the raw string preserved as the label — never
// silently mapped to a semantic accent that means something else.
type StatusEntry = { accent: string; label: string };
const SESSION_STATUS: Record<string, StatusEntry> = {
  draft:                    { accent: B13Status.baseline.accent,     label: "Draft" },          // amber — pre-teaching
  in_progress:              { accent: B13Status.active.accent,       label: "In progress" },    // blue — teaching
  completed:                { accent: B13Status.closed.accent,       label: "Completed" },      // green — done
  awaiting_review:          { accent: B13.primary,                    label: "Awaiting review" },// blue-primary
  reviewed:                 { accent: B13Status.maintenance.accent,  label: "Reviewed" },       // violet — accepted
  returned_for_correction:  { accent: B13Status.hold.accent,         label: "Returned" },       // orange — paused
  voided:                   { accent: B13Status.waiting.accent,      label: "Voided" },         // slate — nothing here
};

export const AbaSessionStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const raw = String(status ?? "").toLowerCase();
  const t = SESSION_STATUS[raw] ?? { accent: B13Ink.ink3, label: raw || "—" };
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      backgroundColor: tint12(t.accent), color: t.accent,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600, lineHeight: 1.2,
      px: 1, py: 0.25,
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: t.accent }} />
      {t.label}
    </Box>
  );
};

// ── Prompt level ────────────────────────────────────────────────────
// Prompt levels are user-configured on the template — every row has
// its own `colour` field set by Kerthiga in the modal. The chip now
// takes the colour directly rather than name-matching against a
// hard-coded English lookup (the tag-hue-hash defect, Batch 13
// Part B §3). Legacy `level: string` prop preserved for callers that
// haven't been re-wired; when only a name is available, the chip
// renders neutral rather than inventing a colour.
export const PromptLevelChip: React.FC<{
  level?: string | null;
  colour?: string | null;
}> = ({ level, colour }) => {
  const c = colour && /^#([0-9a-f]{3}){1,2}$/i.test(colour) ? colour : null;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      backgroundColor: c ? tint12(c) : B13.canvas,
      color: c ?? B13Ink.ink3,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600, lineHeight: 1.2,
      px: 0.75, py: 0.25,
    }}>
      {c && <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: c }} />}
      {level || "—"}
    </Box>
  );
};

// ── Measurement ────────────────────────────────────────────────────
// Neutral chip — measurement type isn't a status or ranking, it's a
// categorisation. Canvas fill + ink2 text.
export const MeasurementChip: React.FC<{ label: string }> = ({ label }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center",
    backgroundColor: B13.canvas, color: B13Ink.ink2,
    borderRadius: `${LegacyTokens.radius.chip}px`,
    fontSize: 12, fontWeight: 500, lineHeight: 1.2,
    px: 1, py: 0.25,
  }}>{label}</Box>
);

// ── Student avatar tile ─────────────────────────────────────────────
// Used on Today's ABA Sessions cards. Renders one- or two-letter initials
// in a brand-tinted 40×40 square with 8px radius. Deterministic accent
// derived from a lightweight hash of the student name so two students in
// a row are visually distinct without introducing a random palette.
const AVATAR_PALETTE = [
  { bg: LegacyTokens.brandSoft,   fg: LegacyTokens.brand },
  { bg: LegacyTokens.tealSoft,    fg: LegacyTokens.teal },
  { bg: LegacyTokens.blueSoft,    fg: LegacyTokens.blue },
  { bg: LegacyTokens.purpleSoft,  fg: LegacyTokens.purple },
  { bg: LegacyTokens.emeraldSoft, fg: LegacyTokens.emerald },
  { bg: LegacyTokens.amberSoft,   fg: LegacyTokens.amberText },
] as const;
function hashPick<T>(key: string, arr: readonly T[]): T {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = ((h << 5) - h + key.charCodeAt(i)) | 0;
  return arr[Math.abs(h) % arr.length];
}
export const StudentAvatar: React.FC<{ name?: string | null; size?: number }> = ({ name, size = 40 }) => {
  const clean = (name || "").trim();
  const initials = clean
    ? clean.split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("")
    : "?";
  const palette = hashPick(clean || "-", AVATAR_PALETTE);
  return (
    <Tooltip title={name || ""} disableHoverListener={!name} arrow>
      <Box sx={{
        width: size, height: size, flexShrink: 0,
        borderRadius: `${LegacyTokens.radius.control}px`,
        backgroundColor: palette.bg, color: palette.fg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.4, fontWeight: 700, letterSpacing: "-0.02em",
      }}>
        {initials}
      </Box>
    </Tooltip>
  );
};
